import { type ChangeEvent, type MouseEvent } from "react"

type ButtonEventType = "button" | "filter" | "tag" | "toggle"
type FormEventType =
  | "submit"
  | "success"
  | "rejected"
  | "error"
  | "validation_error"

type EventName =
  | "subscription"
  | "filter_click"
  | "button_click"
  | "tag_click"
  | "toggle_click"
  | "form_submit"
  | "form_success"
  | "form_rejected"
  | "form_error"
  | "form_validation_error"
  | "link_click"
  | "link_click_external"
  | "link_click_mailto"

type FormName =
  | "sign_up"
  | "payment_success"
  | "pro_subscription"
  | "newsletter_signup"
  | "log_in"
  | "password_recovery"
  | "onboarding"
  | "password_reset"
  | "password_change"
  | "user_info"
  | "unsubscribe"

// Minimize the payloads. Remember that eg. url/pathname (along with eg. project name) is always included in analytics hit
type AnalyticsBasePayload = {
  event: EventName
  context?: "table" | "chart" | "form" | "navigation"
  contextDetail?: "header" | "footer" | "customize" // Use header/footer to place the event more accurately and customize to categorise as customizing of the context
  section?: string // basically h2 or some main heading
  values?: string
}

type AnalyticsTextPayload = AnalyticsBasePayload & {
  text: string
  name?: string // for example "zoom". Ie. name of radio button group
}

type AnalyticsNamePayload = AnalyticsBasePayload & {
  name: string // identifier
}

interface AnalyticsOneOffPayload {
  event: "subscription" | "agChartCreated"
  [key: string]: string
}

type AnalyticsPayload =
  | AnalyticsTextPayload
  | AnalyticsNamePayload
  | AnalyticsOneOffPayload

type WindowWithDataLayer = Window & {
  dataLayer: AnalyticsPayload[]
}

declare const window: WindowWithDataLayer

const gaEvent = (payload: AnalyticsPayload) => {
  try {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(payload)
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Failed to send analytics event", error, { payload })
    }
  }
}

export type AnalyticsBaseEvent = Omit<Partial<AnalyticsTextPayload>, "event">

export type AnalyticsButtonEventProps = AnalyticsBaseEvent & {
  type: ButtonEventType
  event?: never
}

export type AnalyticsChangeEventProps = AnalyticsBaseEvent & {
  type?: "filter"
  event?: never
  text: string
}

export type AnalyticsButtonEventType =
  | MouseEvent<HTMLElement>
  | ChangeEvent<HTMLElement>

export type GaButtonEventTrigger = (
  p: AnalyticsButtonEventProps,
  e: AnalyticsButtonEventType
) => void

export type GaChangeEventTrigger = (p: AnalyticsChangeEventProps) => void

const getTextFromEvent = (e: AnalyticsButtonEventType) =>
  e.currentTarget.textContent ??
  e.currentTarget.getAttribute("aria-label") ??
  e.currentTarget.getAttribute("title") ??
  "no-text-found"

export const gaButtonEvent: GaButtonEventTrigger = (
  { type, text: givenText, ...payload },
  e
) => {
  const eventName = `${type}_click` as EventName
  let text = givenText
  if (!text) {
    try {
      // If ready text is not given, get it from the click target. Remember that buttons can have eg. svgs inside them as well
      text = getTextFromEvent(e)
    } catch (error) {
      // just to make sure analytics can't crash the app even if something unexpected happens or types are forced for example
      text = "no-text-found"
    }
  }
  gaEvent({
    ...payload,
    text,
    event: eventName,
  })
}

interface AnalyticsFormEvent {
  section?: string // basically h2 or some main heading
  type: FormEventType
  name: FormName
  values?: string
}

export const gaFormEvent = ({ type, ...payload }: AnalyticsFormEvent) => {
  const eventName = `form_${type}` as EventName

  gaEvent({
    ...payload,
    event: eventName,
  })
}

// To send one off events such as "payment successful" which are not context aware and might be even considered scrappy. Separate function to keep events controlled by types
export const gaOneOffEvent = (payload: AnalyticsOneOffPayload) => {
  gaEvent(payload)
}

export type AnalyticsLinkEventProps = AnalyticsBaseEvent & {
  type?: "external" | "mailto"
  event?: never
}

export type GaLinkEventTrigger = (
  p: AnalyticsLinkEventProps,
  e: AnalyticsButtonEventType
) => void
